import React, { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'lodash-es';

import ReducerConstants from '../../../_shared/constants/reducers';
import { initialState as tradeDetailsInitialState } from '../../../redux/slices/tradeDetails';
import {
  bidSlice,
  initialState as bidInitialState
} from '../../../redux/slices/bid';
import { BidStatus, BidStatusName } from '../../../_shared/types/auction';
import BidBox from '../BidBox';
import BidModal from '../../BidModal';
import BidService from '../../../services/BidService';
import { TradeState, TradeViewVariant } from '../../../_shared/types/trade';
import isWeb5VdpEnabled from './isWeb5VdpEnabled';
import BidBoxContext from './BidBoxContext';
import { SseEventType } from '../../../_shared/constants/sse';

import ROUTES from '../../Routes/constants';

const BidBoxContainer = ({
  isOMP,
  tradeContext,
  tradeDetails,
  tradeId,
  handleOmpBidding
}) => {
  const [bidModalRef, setBidModalRef] = useState(null);
  const bidBoxContext = {
    ...bidModalRef,
    isSelling: tradeDetails?.isSelling,
    tradeDetails
  };

  const onRefChange = useCallback(
    node => {
      if (node && !isEqual(node, bidModalRef)) {
        setBidModalRef(node);
      }
    },
    [bidModalRef]
  );

  /**
   * Store vars
   */
  const tradeDetailsState = useSelector(
    state => state[ReducerConstants.TRADE_DETAILS],
    shallowEqual
  );
  const bidList = useSelector(
    state => state[ReducerConstants.BID_LIST],
    shallowEqual
  );
  const user = useSelector(
    state => state[ReducerConstants.AUTH]?.user,
    shallowEqual
  );
  const liveBid = useSelector(
    state => state[ReducerConstants.LIVE_BIDS][tradeId],
    shallowEqual
  );

  // Below slices only exists on OMP
  const proxyBid = useSelector(
    state => isOMP && state[ReducerConstants.PROXY_BIDS][tradeId],
    shallowEqual
  );
  const biddingData = useSelector(
    state => isOMP && state[ReducerConstants.BIDDING_DATA],
    shallowEqual
  );

  /**
   * Services
   */
  const bidService = BidService.get();
  const dispatch = useDispatch();

  /**
   * State vars
   */
  const [isLoading, setIsLoading] = useState(true);
  const [bidStatus, setBidStatus] = useState(null);
  const [bids, setBids] = useState(bidInitialState);

  const { bidContext, setBidContext, bidModalOpen } = bidModalRef ?? {};

  const setActiveExpiryTime = useCallback(
    time => {
      if (setBidContext) {
        setBidContext(context => ({ ...context, activeExpiryTime: time }));
      }
    },
    [setBidContext]
  );

  const setIsTimerExtended = useCallback(() => {
    if (setBidContext) {
      setBidContext(context => ({ ...context, isTimerExtended: true }));
    }
  }, [setBidContext]);

  const shouldWarnSameDealerDGN = useCallback(
    (tradeDetailsSummary, _user, _bidStatus) => {
      const isInSameDealerShip = (topBidder, currentUser) => {
        if (!topBidder || !currentUser) {
          return false;
        }

        const currentDealerId = currentUser?.companyId;

        return (
          currentDealerId === topBidder?.companyId &&
          currentUser.id !== topBidder?.id
        );
      };

      const isInSameDealerGroup = (topBidder, currentUser) => {
        if (!topBidder || !currentUser) {
          return false;
        }

        const isSameDGN = topBidder?.dealerGroupIds?.some(id =>
          currentUser?.dealerGroupIds?.includes(id)
        );

        return (
          topBidder?.dealerGroupIds?.length &&
          (isSameDGN || topBidder?.isSameDealerGroup) &&
          topBidder?.id !== currentUser.id
        );
      };

      const { topBidderSummary: topBidder } =
        tradeDetailsSummary?.auctionSummary || {};
      let isOutBidSameDealer = false;
      let isOutBidSameDGN = false;

      if (_bidStatus === BidStatus.topBidder) {
        isOutBidSameDealer = false;
        isOutBidSameDGN = false;
      } else if (isInSameDealerShip(topBidder, _user)) {
        isOutBidSameDealer = true;
        isOutBidSameDGN = false;
      } else if (isInSameDealerGroup(topBidder, _user)) {
        isOutBidSameDealer = false;
        isOutBidSameDGN = true;
      }
      if (setBidContext) {
        setBidContext(context => ({
          ...context,
          isOutBidSameDealer,
          isOutBidSameDGN
        }));
      }
    },
    [setBidContext]
  );

  const getBidList = useCallback(
    async id => {
      const bidListResponse = await bidService.getBidList(id);
      return bidListResponse;
    },
    [bidService]
  );

  /**
   * Process Trade Details
   */
  useEffect(() => {
    if (
      tradeDetailsState !== tradeDetailsInitialState &&
      tradeDetailsState?.id === tradeId &&
      setBidContext
    ) {
      const combinedTradeDetails = {
        ...tradeDetailsState,
        ...tradeDetailsState.details
      };

      const web5VdpEnabled = isWeb5VdpEnabled(
        tradeDetailsState,
        tradeContext,
        user
      );

      if (
        [TradeState.active].includes(tradeDetailsState?.tradeState) ||
        (web5VdpEnabled &&
          [TradeState.pending, TradeState.closing].includes(
            tradeDetailsState?.tradeState
          ))
      ) {
        // Wait for trade details call to be made before fetching bid list
        if (tradeDetails.id) {
          getBidList(tradeId).then(response => {
            dispatch(bidSlice.actions.setBidList(response));
          });
        }

        combinedTradeDetails.currentProxyAmount =
          tradeDetailsState?.auctionSummary?.proxyAmount;
        combinedTradeDetails.numberOfBids =
          tradeDetailsState?.auctionSummary?.numBids;

        if (
          tradeDetailsState?.auctionSummary?.userBidStatus === BidStatus.outbid
        ) {
          setBidStatus(BidStatus.outbid);
          combinedTradeDetails.variant = BidStatusName[BidStatus.outbid];
          combinedTradeDetails.currentProxyAmount = null;
        } else if (
          tradeDetailsState?.auctionSummary?.userBidStatus ===
            BidStatus.topBidder ||
          tradeDetailsState?.auctionSummary?.userBidStatus === BidStatus.proxy
        ) {
          setBidStatus(BidStatus.topBidder);
          combinedTradeDetails.variant = BidStatusName[BidStatus.topBidder];
        }

        if (!isOMP) {
          shouldWarnSameDealerDGN(
            tradeDetailsState,
            user,
            tradeDetailsState?.auctionSummary?.userBidStatus
          );
        }
      } else if (tradeDetailsState.tradeState === TradeState.upcoming) {
        setBidContext(context => ({
          ...context,
          currentUpcomingProxy: tradeDetailsState?.details?.upcomingProxy
        }));
        setIsLoading(false);
      } else if (!isOMP && !window.location.href.includes(ROUTES.BUYING)) {
        // Take the user to TR4 for non-Active/Upcoming states
        window.location.replace(`${ROUTES.TR4_ENDPOINT}trade/${tradeId}`);
      }

      // Destructure the 'details' param to normalize shape with tradeSummary in Trade List
      setBidContext(context => ({
        ...context,
        ...combinedTradeDetails
      }));
    }
  }, [
    dispatch,
    getBidList,
    isOMP,
    setBidContext,
    shouldWarnSameDealerDGN,
    tradeContext,
    tradeDetails.id,
    tradeDetailsState,
    tradeId,
    user
  ]);

  /**
   * Process Bid History
   */
  useEffect(() => {
    if (bidList !== bidInitialState && tradeDetailsState?.id === tradeId) {
      // TODO: Add banners for dealership high bidder/outbid
      setIsLoading(false);
      setBids(bidList);
    }
  }, [bidList, tradeDetailsState?.id, tradeId]);

  /**
   * Process proxy event from OMP
   */
  useEffect(() => {
    if (isOMP && setBidContext && proxyBid) {
      setBidContext(context => ({
        ...context,
        auctionSummary: {
          ...context.auctionSummary,
          biddingDealerId: proxyBid.bidderDealerId,
          userBidStatus: BidStatus.proxy
        },
        currentProxyAmount: proxyBid.proxyBidAmount,
        ...(tradeDetailsState?.tradeState === TradeState.upcoming && {
          currentUpcomingProxy: {
            proxyAmount: proxyBid.proxyBidAmount,
            bidAmount: proxyBid.bidAmount
          }
        })
      }));
    }
  }, [isOMP, proxyBid, setBidContext, tradeDetailsState?.tradeState]);

  /**
   * Process proxy live event
   * TODO: Remove this as a follow-up to search-ui change to use proxyBids slice
   */
  useEffect(() => {
    if (
      setBidContext &&
      [
        SseEventType.ACTIVE_PROXY_BID_EVENT,
        SseEventType.UPCOMING_PROXY_BID_EVENT
      ].includes(liveBid?.eventType)
    ) {
      setBidContext(context => ({
        ...context,
        auctionSummary: {
          ...context.auctionSummary,
          biddingDealerId: liveBid.bidderDealerId,
          userBidStatus: BidStatus.proxy
        },
        currentProxyAmount: liveBid.proxyBidAmount,
        ...(tradeDetailsState?.tradeState === TradeState.upcoming && {
          currentUpcomingProxy: {
            proxyAmount: liveBid.proxyBidAmount,
            bidAmount: liveBid.bidAmount
          }
        })
      }));
    }
  }, [liveBid, setBidContext, tradeDetailsState?.tradeState]);

  /**
   * Process bidding live event
   */
  useEffect(() => {
    if ([TradeState.pending].includes(tradeDetailsState?.tradeState)) {
      setActiveExpiryTime(tradeDetailsState?.statusDates?.pendingExpiry);
    }
    if ([TradeState.closing].includes(tradeDetailsState?.tradeState)) {
      setActiveExpiryTime(tradeDetailsState?.statusDates?.closing);
    }
    if (
      !setBidContext ||
      ![TradeState.active, TradeState.pending, TradeState.closing].includes(
        tradeDetailsState?.tradeState
      ) ||
      !liveBid ||
      liveBid?.eventType !== SseEventType.BID_EVENT ||
      liveBid?.bidCount < bidContext?.numberOfBids
    ) {
      return;
    }

    // TODO: Add banners for dealership high bidder/outbid
    setBidContext(context => ({
      ...context,
      auctionSummary: {
        ...context.auctionSummary,
        numBids: liveBid.bidCount,
        maxBid: liveBid.maxBidAmount,
        topBidderSummary: liveBid.topBidder,
        isTimerExtended: liveBid.isTimerExtended,
        ...(liveBid.bidderDealerId && {
          biddingDealerId: liveBid.bidderDealerId
        }),
        userBidStatus:
          liveBid.topBidder?.id === user?.id
            ? BidStatus.topBidder
            : BidStatus.outbid
      },
      currentBidAmount: liveBid.maxBidAmount,
      numberOfBids: liveBid.bidCount
    }));

    if (
      bidContext?.currentProxyAmount &&
      liveBid.maxBidAmount >= bidContext?.currentProxyAmount
    ) {
      setBidContext(context => ({
        ...context,
        isSettingProxy: false,
        currentProxyAmount: null
      }));
    }

    if (
      liveBid.topBidderId === user?.id ||
      liveBid?.topBidder?.id === user?.id
    ) {
      setBidStatus(BidStatus.topBidder);
      setBidContext(context => ({
        ...context,
        bidStatus: BidStatus.topBidder,
        variant: BidStatusName[BidStatus.topBidder]
      }));
    } else if (
      bidStatus === BidStatus.topBidder &&
      (liveBid.topBidderId !== user?.id || liveBid.topBidder?.id !== user?.id)
    ) {
      setBidStatus(BidStatus.outbid);
      setBidContext(context => ({
        ...context,
        bidStatus: BidStatus.outbid,
        variant: BidStatusName[BidStatus.outbid]
      }));
    } else if (
      bidList?.users &&
      bidList?.users.length &&
      bidList?.users?.some(bidUsers => user?.id === bidUsers.id) &&
      bidList?.users?.some(bidUsers => user?.companyId === bidUsers?.companyId)
    ) {
      if (
        liveBid.topBidderId === user?.id ||
        liveBid.topBidder?.id === user?.id
      ) {
        setBidStatus(BidStatus.topBidder);
        setBidContext(context => ({
          ...context,
          bidStatus: BidStatus.topBidder,
          variant: BidStatusName[BidStatus.topBidder]
        }));
      } else {
        setBidStatus(BidStatus.outbid);
        setBidContext(context => ({
          ...context,
          bidStatus: BidStatus.outbid,
          variant: BidStatusName[BidStatus.outbid]
        }));
      }
    }

    if (!isOMP) {
      shouldWarnSameDealerDGN(tradeDetailsState, user, bidStatus);
    }

    // Update the timer
    setActiveExpiryTime(liveBid.activeExpiryTime);
    if (
      (liveBid?.isTimerExtended ||
        tradeDetailsState?.auctionSummary?.isTimerExtended) &&
      !bidContext?.isTimerExtended
    ) {
      setIsTimerExtended();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    isOMP,
    setBidContext,
    liveBid,
    bidStatus,
    user,
    bidContext?.currentProxyAmount,
    bidList?.users,
    shouldWarnSameDealerDGN,
    setActiveExpiryTime,
    setIsTimerExtended,
    tradeDetailsState
  ]);

  /**
   * Process User
   */
  useEffect(() => {
    if (typeof bidContext?.isCsu === 'boolean') {
      return;
    }

    if (setBidContext) {
      if (user?.superUserId && user?.superUserId !== user?.id) {
        setBidContext(context => ({ ...context, isCsu: true }));
      } else {
        setBidContext(context => ({ ...context, isCsu: false }));
      }
    }
  }, [setBidContext, user?.superUserId, user?.id, bidContext?.isCsu]);

  useEffect(() => {
    if (
      [TradeState.closing, TradeState.active].includes(
        tradeDetailsState?.tradeState
      ) &&
      bidContext?.currentProxyAmount
    ) {
      setBidContext(context => ({ ...context, isSettingProxy: true }));
    }
  }, [
    setBidContext,
    tradeDetailsState?.tradeState,
    bidContext?.currentProxyAmount
  ]);

  useEffect(() => {
    if (
      [TradeState.closing, TradeState.active].includes(
        tradeDetailsState?.tradeState
      ) &&
      bidContext?.currentProxyAmount !== undefined &&
      bidContext?.currentBidAmount === bidContext?.currentProxyAmount
    ) {
      setBidContext(context => ({
        ...context,
        isSettingProxy: false,
        currentProxyAmount: undefined
      }));
    }
  }, [
    setBidContext,
    tradeDetailsState?.tradeState,
    bidContext?.currentProxyAmount,
    bidContext?.currentBidAmount
  ]);

  useEffect(() => {
    if (!bidModalOpen && !bidContext?.currentProxyAmount && setBidContext) {
      setBidContext(context => ({ ...context, isSettingProxy: false }));
    }
  }, [setBidContext, bidModalOpen, bidContext?.currentProxyAmount]);

  useEffect(() => {
    if (!bidContext?.bidStatus && bidStatus && setBidContext) {
      setBidContext(context => ({
        ...context,
        bidStatus
      }));
    }
  }, [bidContext?.bidStatus, setBidContext, bidStatus]);

  useEffect(() => {
    if (bids?.bids?.length !== bidContext?.bidList?.bids?.length) {
      setBidContext(context => ({ ...context, bidList: bids }));
    }
  }, [bidContext?.bidList, setBidContext, bids]);

  useEffect(() => {
    if (
      ![TradeState.closing, TradeState.active].includes(
        tradeDetailsState?.tradeState
      ) ||
      !liveBid
    ) {
      return;
    }

    // OMP Race condition: If biddingData contains updated bid amount, then bid list is already being updated by useUpdateBidContextWithApi
    const hasUpdatedBiddingData =
      biddingData?.tradeId === tradeId &&
      biddingData?.auctionSummary?.numBids >= liveBid?.bidCount;

    // Refetch bid list when liveBid tradeId matches tradeId user is on.
    if (
      liveBid.tradeId === tradeId &&
      liveBid?.eventType === SseEventType.BID_EVENT &&
      !hasUpdatedBiddingData
    ) {
      getBidList(tradeId).then(response => {
        dispatch(bidSlice.actions.setBidList(response));
      });
    }
  }, [
    tradeDetailsState?.tradeState,
    liveBid,
    dispatch,
    bidService,
    tradeId,
    getBidList,
    biddingData?.tradeId,
    biddingData?.auctionSummary?.numBids
  ]);

  /**
   * TODO: Replace this mess with context
   */
  return (
    <BidBoxContext.Provider value={bidBoxContext}>
      <BidBox
        isLoading={isLoading}
        isOMP={isOMP}
        location={TradeViewVariant.VDP}
        tradeContext={tradeContext}
        handleOmpBidding={handleOmpBidding}
      />
      <BidModal
        ref={onRefChange}
        isOMP={isOMP}
        location={TradeViewVariant.VDP}
        handleOmpBidding={handleOmpBidding}
      />
    </BidBoxContext.Provider>
  );
};

export default BidBoxContainer;
